import { forwardRef, useImperativeHandle, useState, useMemo } from 'react';
import { Form, message, Modal } from "antd";
import { useRequest } from "ahooks";
import { EditForm, FormConfigType } from "../../../components/editForm";
import { AmountServer } from "../../../server";

const server = AmountServer.InternationalRemittanceLimitList;

const DEFAULT_OPERATION_DATA = {
    ChannelCreditedTimeId: undefined,
    CountryId: undefined,
    ExchangeFeeRate: '',
    ExchangeFeeDiscount: '',
    ServiceFee: '',
    ServiceFeeDiscount: '',
    MaxLimit: '',
    MinLimit: '',
    Sort: '',
    WorkTime: '',
    WorkTimeEn: '',
}

type OperateModeType = 'create' | 'update'

export const Operation = forwardRef((props: any, ref: any) => {
    const { onSuccess, options } = props;
    const [loading, setLoading] = useState<boolean>(false);
    const [visible, setVisiable] = useState<boolean>(false);
    const [formData, setFormData] = useState<any>({ ...DEFAULT_OPERATION_DATA });
    const [form] = Form.useForm();
    const [mode, setMode] = useState<OperateModeType>('create');

    useImperativeHandle(ref, () => ({
        changeVisible
    }))

    const changeVisible = (val: any, mode?: OperateModeType) => {
        if (val) {
            const data = {
                ...DEFAULT_OPERATION_DATA,
                ...(mode === 'update' ? val : {})
            };
            mode && setMode(mode)
            form?.resetFields?.();
            setFormData(data);
        } else {
            form?.resetFields?.();
        }
        setVisiable(!!val);
    }

    const { run } = useRequest(mode === 'create' ? server.create : server.update, {
        manual: true,
        onBefore: () => setLoading(true),
        onFinally: () => setLoading(false),
        onError: () => changeVisible(false),
        onSuccess: (res: any) => {
            message.success(mode === 'create' ? '新增成功' : '更新成功');
            changeVisible(false);
            onSuccess?.();
        }
    });

    const getBaseInfoFormConfig: FormConfigType = useMemo((): FormConfigType => {
        if (mode === 'update') {
            return [
                {
                    title: "手续费",
                    field: "ExchangeFeeRate",
                    type: "input",
                    placeholder: "请输入手续费比例",
                    required: true,
                },
                {
                    title: "手续费限时减免",
                    field: "ExchangeFeeDiscount",
                    type: "input",
                    placeholder: "请输入手续费限时减免",
                    required: true,
                },
                {
                    title: "通道费",
                    field: "ServiceFee",
                    type: "input",
                    placeholder: "请输入通道费",
                    required: true,
                },
                {
                    title: "通道费限时减免",
                    field: "ServiceFeeDiscount",
                    type: "input",
                    placeholder: "请输入通道费限时减免",
                    required: true,
                },
                {
                    title: "最小限额",
                    field: "MinLimit",
                    type: "input",
                    placeholder: "请输入最小限额",
                    required: true,
                },
                {
                    title: "最大限额",
                    field: "MaxLimit",
                    type: "input",
                    placeholder: "请输入最大限额",
                    required: true,
                },
				{
				    title: "排序",
				    field: "Sort",
				    type: "inputNumber",
				    placeholder: "请输入排序",
				    required: true,
				},
				{
				    title: "工作时间",
				    field: "WorkTime",
				    type: "input",
				    placeholder: "请输入工作时间",
				    required: true,
				},
				{
				    title: "工作时间英文",
				    field: "WorkTimeEn",
				    type: "input",
				    placeholder: "请输入工作时间英文",
				    required: true,
				},
            ];
        }
        return [
            {
                title: "通道时效",
                field: "ChannelCreditedTimeId",
                type: "select",
                placeholder: "请选择通道时效",
                options: options?.creditedTimeOptions || [],
                fieldNames: { label: 'name', value: 'id' },
                allowClear: true,
                required: true,
            },
            {
                title: "国家",
                field: "CountryId",
                type: "select",
                placeholder: "请选择国家",
                options: options?.countryOptions || [],
                fieldNames: { label: 'name', value: 'id' },
                allowClear: true,
                required: true,
            },
            {
                title: "手续费",
                field: "ExchangeFeeRate",
                type: "input",
                placeholder: "请输入手续费比例",
                required: true,
            },
            {
                title: "手续费限时减免",
                field: "ExchangeFeeDiscount",
                type: "input",
                placeholder: "请输入手续费限时减免",
                required: true,
            },
            {
                title: "通道费",
                field: "ServiceFee",
                type: "input",
                placeholder: "请输入通道费",
                required: true,
            },
            {
                title: "通道费限时减免",
                field: "ServiceFeeDiscount",
                type: "input",
                placeholder: "请输入通道费限时减免",
                required: true,
            },
            {
                title: "最小限额",
                field: "MinLimit",
                type: "input",
                placeholder: "请输入最小限额",
                required: true,
            },
            {
                title: "最大限额",
                field: "MaxLimit",
                type: "input",
                placeholder: "请输入最大限额",
                required: true,
            },
            {
                title: "排序",
                field: "Sort",
                type: "inputNumber",
                placeholder: "请输入排序",
                required: true,
            },
            {
                title: "工作时间",
                field: "WorkTime",
                type: "input",
                placeholder: "请输入工作时间",
                required: true,
            },
            {
                title: "工作时间英文",
                field: "WorkTimeEn",
                type: "input",
                placeholder: "请输入工作时间英文",
                required: true,
            },
        ];
    }, [options, mode]);

    return (
        <div>
            <Modal
                title={mode === 'create' ? '新增限额' : '编辑限额'}
                width={500}
                closeIcon={null}
                maskClosable={false}
                open={visible}
                onCancel={() => changeVisible(false)}
                okButtonProps={{ loading }}
                onOk={async () => {
                    await form.validateFields();
                    let submitData: any = {};
                    if (mode === 'create') {
                        submitData = {
                            ChannelCreditedTimeId: formData.ChannelCreditedTimeId,
                            CountryId: formData.CountryId,
                            ExchangeFeeRate: formData.ExchangeFeeRate,
                            ExchangeFeeDiscount: formData.ExchangeFeeDiscount,
                            ServiceFee: formData.ServiceFee,
                            ServiceFeeDiscount: formData.ServiceFeeDiscount,
                            MaxLimit: formData.MaxLimit,
                            MinLimit: formData.MinLimit,
                            Sort: formData.Sort,
                            WorkTime: formData.WorkTime,
                            WorkTimeEn: formData.WorkTimeEn,
                        };
                    } else {
                        submitData = {
                            Id: formData.Id,
                            ExchangeFeeRate: formData.ExchangeFeeRate,
                            ExchangeFeeDiscount: formData.ExchangeFeeDiscount,
                            ServiceFee: formData.ServiceFee,
                            ServiceFeeDiscount: formData.ServiceFeeDiscount,
                            MaxLimit: formData.MaxLimit,
                            MinLimit: formData.MinLimit,
                            Sort: formData.Sort,
                            WorkTime: formData.WorkTime,
                            WorkTimeEn: formData.WorkTimeEn,
                        };
                    }
                    run(submitData);
                }}
            >
                <EditForm
                    componentId="limit-operation"
                    className="mt20"
                    form={form}
                    formAttrsProps={{
                        labelCol: { span: 8 },
                        wrapperCol: { span: 16 },
                    }}
                    formData={formData}
                    onDataChange={(data: any) => {
                        setFormData(data);
                    }}
                    formConfig={getBaseInfoFormConfig}
                />
            </Modal>
        </div>
    );
})
